/**
 * @architecture-header 1
 * @layer module
 * @domain classrooms
 * @responsibility Render Classroom collection states and teacher-owned Desktop/Mobile creation compositions.
 * @state-owner TanStack Query plus form-local logical command and input state
 * @allowed-imports React; React Hook Form; TanStack Query; app environment/role provider; same-module hooks/schema; shared error, overlay, loading, and workspace state
 * @forbidden routes; direct API client; permission inference
 * @public-api classrooms module
 * @side-effects refetches the Classroom collection, opens an overlay, creates a Classroom, and selects its projected row
 * @tests ClassroomCreateAction.test.tsx and workspace Playwright flows
 * @security-boundary browser form commands cross the authenticated API boundary
 * @data-classification tenant-scoped Classroom name and optional subject; no credentials
 * @trust-model form and active-context inputs are untrusted; schema validation is local convenience and backend authorization remains authoritative
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { type ReactNode, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useWorkspaceEnvironment } from '../../../app/config/environment';
import { useActiveContextId } from '../../../app/providers/ActiveRoleProvider';
import { ModalDocument } from '../../../shared/components/Modal';
import { useOverlay } from '../../../shared/components/OverlayHost';
import { VispoLoading } from '../../../shared/components/WorkspaceState';
import { hasHttpStatus } from '../../../shared/lib/api-error';
import { useDatasetStore } from '../../../shared/model/dataset-store';
import { useIsMobile, useTranslator } from '../../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';
import { useCreateClass } from '../api/classrooms.mutations';
import { useClassrooms } from '../api/classrooms.queries';
import { type CreateClassValues, createClassSchema } from '../schemas/class-actions';

export type ClassroomCreateSurface =
  | 'desktop-empty'
  | 'desktop-list'
  | 'mobile-empty'
  | 'mobile-list';

type ClassroomFormSurface = 'desktop' | 'mobile-list';

const EMPTY_ROSTER_SEATS = [
  'front-left',
  'front-right',
  'middle-left',
  'middle-right',
  'back-left',
  'back-right',
  'last-left',
  'last-right',
] as const;

function newIdempotencyKey(): string {
  return (
    globalThis.crypto?.randomUUID?.() ??
    `classroom-${Date.now()}-${Math.random().toString(36).slice(2)}`
  );
}

function createErrorMessage(cause: unknown): string {
  if (hasHttpStatus(cause, 403)) {
    return '你沒有在這個工作區建立班級的權限。';
  }
  return cause instanceof Error ? cause.message : '班級建立失敗，請稍後再試。';
}

function collectionErrorMessage(cause: unknown): string {
  if (hasHttpStatus(cause, 403)) {
    return '你沒有查看這個工作區班級的權限。';
  }
  return cause instanceof Error ? cause.message : '班級清單暫時無法載入。';
}

function ClassroomCreateForm({ surface }: { surface: ClassroomFormSurface }) {
  const t = useTranslator();
  const activeContextId = useActiveContextId();
  const mutation = useCreateClass();
  const { transientNavigation } = useWorkspaceEnvironment();
  const { closeModal } = useOverlay();
  const hydrate = useDatasetStore((state) => state.hydrate);
  const pick = useWorkspaceUiStore((state) => state.pick);
  const notify = useWorkspaceUiStore((state) => state.notify);
  const form = useForm<CreateClassValues>({
    defaultValues: { name: '', subject: '' },
    resolver: zodResolver(createClassSchema),
  });
  const nameInput = useRef<HTMLInputElement>(null);
  const logicalCommand = useRef({ fingerprint: '', idempotencyKey: newIdempotencyKey() });
  const nameField = form.register('name');

  useEffect(() => nameInput.current?.focus(), []);

  useEffect(() => {
    if (surface !== 'mobile-list') return;
    return transientNavigation?.open('classroom-create', closeModal);
  }, [closeModal, surface, transientNavigation]);

  const submit = form.handleSubmit(async (values) => {
    const fingerprint = JSON.stringify([values.name, values.subject ?? '']);
    if (logicalCommand.current.fingerprint && logicalCommand.current.fingerprint !== fingerprint) {
      logicalCommand.current.idempotencyKey = newIdempotencyKey();
    }
    logicalCommand.current.fingerprint = fingerprint;
    try {
      const classroom = await mutation.mutateAsync({
        activeContextId,
        idempotencyKey: logicalCommand.current.idempotencyKey,
        values,
      });
      closeModal();
      const currentClasses = useDatasetStore.getState().classes;
      hydrate({
        classes: [
          {
            code: classroom.joinCode,
            id: classroom.id,
            name: classroom.name,
            students: classroom.students,
            subtitle: classroom.subject,
          },
          ...currentClasses.filter((candidate) => candidate.id !== classroom.id),
        ],
      });
      pick('c0', surface === 'mobile-list');
      notify(t('班級已建立'));
    } catch {
      // Mutation state owns the user-facing error below.
    }
  });

  const fields = (
    <div className="classroom-create-fields">
      <label className="classroom-create-field">
        <span>{t('班級名稱')}</span>
        <input
          aria-describedby={form.formState.errors.name ? 'classroom-create-name-error' : undefined}
          aria-invalid={Boolean(form.formState.errors.name)}
          maxLength={160}
          placeholder={t('例如：週五自然班')}
          {...nameField}
          ref={(element) => {
            nameField.ref(element);
            nameInput.current = element;
          }}
        />
        {form.formState.errors.name ? (
          <em id="classroom-create-name-error">{t(form.formState.errors.name.message ?? '')}</em>
        ) : null}
      </label>
      <label className="classroom-create-field">
        <span>{`${t('科目')} · ${t('選填')}`}</span>
        <input maxLength={160} placeholder={t('例如：自然科學')} {...form.register('subject')} />
        {form.formState.errors.subject ? (
          <em>{t(form.formState.errors.subject.message ?? '')}</em>
        ) : null}
      </label>
      {mutation.isError ? (
        <p className="classroom-create-error" role="alert">
          {t(createErrorMessage(mutation.error))}
        </p>
      ) : null}
    </div>
  );

  const actions = (
    <>
      <button className="bt" disabled={mutation.isPending} type="button" onClick={closeModal}>
        {t('取消')}
      </button>
      <button className="bt pri" disabled={mutation.isPending} type="submit">
        {mutation.isPending ? t('正在建立…') : t('建立並開啟')}
      </button>
    </>
  );

  if (surface === 'mobile-list') {
    return (
      <section
        aria-label={t('建立班級')}
        aria-modal="true"
        className="classroom-create-mobile-sheet"
        role="dialog"
      >
        <form className="classroom-create-mobile-sheet__form" onSubmit={submit}>
          <header>
            <button aria-label={t('取消')} type="button" onClick={closeModal}>
              ×
            </button>
            <div>
              <h2>{t('建立班級')}</h2>
              <p>{t('建立後會直接開啟新班級。')}</p>
            </div>
          </header>
          <div className="classroom-create-mobile-sheet__body">{fields}</div>
          <footer>{actions}</footer>
        </form>
      </section>
    );
  }

  return (
    <div
      aria-label={t('建立班級')}
      aria-modal="true"
      className="classroom-create-dialog"
      role="dialog"
    >
      <form onSubmit={submit}>
        <ModalDocument
          className="classroom-create-document"
          footer={actions}
          maxWidth={520}
          subtitle={t('填入班級名稱，科目之後也可以再補。')}
          title={t('建立班級')}
        >
          {fields}
        </ModalDocument>
      </form>
    </div>
  );
}

export function ClassroomCreateAction({ surface }: { surface: ClassroomCreateSurface }) {
  const t = useTranslator();
  const activeContextId = useActiveContextId();
  const classroomsQuery = useClassrooms(activeContextId);
  const projectedStatus = useDatasetStore((state) => state.classroomDirectoryStatus);
  const projectedClassCount = useDatasetStore((state) => state.classes.length);
  const { isModalOpen, openModal } = useOverlay();
  const mobile = surface.startsWith('mobile');
  const empty = surface.endsWith('empty');
  const trigger = useRef<HTMLButtonElement>(null);
  const openedCreateForm = useRef(false);

  useEffect(() => {
    if (!openedCreateForm.current || isModalOpen) return;
    openedCreateForm.current = false;
    trigger.current?.focus();
  }, [isModalOpen]);

  if (
    classroomsQuery.isPending ||
    projectedStatus === 'loading' ||
    projectedStatus === 'denied' ||
    (classroomsQuery.isError && hasHttpStatus(classroomsQuery.error, 403))
  ) {
    return null;
  }

  if (classroomsQuery.isError || projectedStatus === 'error') return null;

  const collectionIsEmpty = projectedClassCount === 0;
  const actionLabel = collectionIsEmpty ? '建立第一個班級' : '建立班級';

  // The empty experience owns its single primary action. Collection chrome stays quiet until it
  // has a Classroom to act on, avoiding duplicate calls to the same form on Desktop and Mobile.
  if (collectionIsEmpty && !empty) return null;

  return (
    <button
      aria-label={t(actionLabel)}
      className={
        empty
          ? 'bt pri classroom-create-action--empty-state'
          : mobile
            ? `mobile-top-bar__icon classroom-create-action--mobile${collectionIsEmpty ? ' classroom-create-action--empty' : ''}`
            : 'bt pri addbt classroom-create-action--icon'
      }
      title={t(actionLabel)}
      type="button"
      onClick={() => {
        openedCreateForm.current = true;
        openModal(<ClassroomCreateForm surface={mobile ? 'mobile-list' : 'desktop'} />);
      }}
      ref={trigger}
    >
      {mobile || !empty ? null : t(actionLabel)}
      <svg aria-hidden="true" viewBox="0 0 24 24">
        <path d="M12 5v14M5 12h14" />
      </svg>
    </button>
  );
}

function ClassroomEmptyOnboarding() {
  const t = useTranslator();
  const mobile = useIsMobile();

  return (
    <section className="classroom-empty-onboarding">
      <div className="classroom-empty-onboarding__content">
        <p className="classroom-empty-onboarding__eyebrow">{t('班級')}</p>
        <h2>{t('還沒有班級')}</h2>
        <p className="classroom-empty-onboarding__description">
          {t('建立班級後，學生、活動與學習記錄會顯示在這裡。')}
        </p>
        <ol className="classroom-empty-steps">
          <li>
            <span>1</span>
            <b>{t('建立班級')}</b>
          </li>
          <li>
            <span>2</span>
            <b>{t('邀請學生')}</b>
          </li>
          <li>
            <span>3</span>
            <b>{t('安排活動')}</b>
          </li>
        </ol>
        <ClassroomCreateAction surface={mobile ? 'mobile-empty' : 'desktop-empty'} />
      </div>
      <div aria-hidden="true" className="classroom-empty-roster">
        <div className="classroom-empty-roster__board" />
        <div className="classroom-empty-roster__seats">
          {EMPTY_ROSTER_SEATS.map((seat) => (
            <span key={seat} />
          ))}
        </div>
        <div className="classroom-empty-roster__desk" />
      </div>
    </section>
  );
}

export function ClassroomCollectionState({
  children,
  surface,
}: {
  readonly children?: ReactNode;
  readonly surface: 'list' | 'main';
}) {
  const t = useTranslator();
  const mobile = useIsMobile();
  const environment = useWorkspaceEnvironment();
  const activeContextId = useActiveContextId();
  const classroomsQuery = useClassrooms(activeContextId);
  const projectedStatus = useDatasetStore((state) => state.classroomDirectoryStatus);
  const projectedClassCount = useDatasetStore((state) => state.classes.length);
  const stateClassName = surface === 'list' ? 'classroom-collection-state--list' : '';

  if (classroomsQuery.isPending || projectedStatus === 'loading') {
    return (
      <VispoLoading
        className={`classroom-collection-state ${stateClassName}`}
        label={t('正在載入班級')}
      />
    );
  }

  const denied =
    projectedStatus === 'denied' ||
    (classroomsQuery.isError && hasHttpStatus(classroomsQuery.error, 403));
  if (denied) {
    return (
      <div className={`classroom-collection-state ${stateClassName}`} role="alert">
        <strong>{t('無法查看班級')}</strong>
        <span>{t('你沒有查看這個工作區班級的權限。')}</span>
      </div>
    );
  }

  if (classroomsQuery.isError || projectedStatus === 'error') {
    return (
      <div className={`classroom-collection-state ${stateClassName}`} role="alert">
        <strong>{t('班級清單暫時無法載入')}</strong>
        <span>{t(collectionErrorMessage(classroomsQuery.error))}</span>
        <button
          className="bt sm"
          type="button"
          onClick={() => {
            environment.testAdapter?.notify('classroomDirectory');
            void classroomsQuery.refetch();
          }}
        >
          {t('重試')}
        </button>
      </div>
    );
  }

  if (projectedClassCount > 0) return children ?? null;

  return surface === 'list' && !mobile ? (
    <div
      aria-label={`${t('班級清單')}：${t('0 個班級')}`}
      className="classroom-collection-status classroom-collection-state--list"
      role="status"
    />
  ) : (
    <ClassroomEmptyOnboarding />
  );
}
